import React, { useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";
import { getDashboardStats } from "../../redux/actions/Dashboard";
import { getChats } from "../../redux/actions/ChatActions";
import Routes from "../../constants/Routes";
import i18nstring, { strings } from "../../i18n";
import {
  YTD,
  buildYearOptions,
  formatDecimal,
  formatMonth,
  getComparisonYear,
  computeDelta
} from "../../utils/stats";
import StatCard from "../atoms/StatCard";
import YearSelector from "../atoms/YearSelector";
import "./OperatorDashboard.scss";

const STAT_CARDS = [
  {
    key: "totalWeight",
    icon: "weight",
    labelKey: strings.DASH_TOTAL_WEIGHT,
    formatter: formatDecimal,
    linkKey: strings.DASH_GO_TO_DONATIONS,
    route: Routes.FOOD_WEIGHT_REVIEW,
    segmentable: true
  },
  {
    key: "activeGardeners",
    icon: "leaf",
    labelKey: strings.DASH_ACTIVE_GARDENERS,
    linkKey: strings.DASH_GO_TO_GARDENERS,
    route: Routes.GARDENER_REVIEW,
    segmentable: true
  },
  {
    key: "totalDonations",
    icon: "archive",
    labelKey: strings.DASH_TOTAL_DONATIONS,
    linkKey: strings.DASH_GO_TO_DONATIONS,
    route: Routes.FOOD_WEIGHT_REVIEW,
    segmentable: true
  },
  {
    key: "newGardeners",
    icon: "user plus",
    labelKey: strings.DASH_NEW_GARDENERS,
    linkKey: strings.DASH_GO_TO_GARDENERS,
    route: Routes.GARDENER_REVIEW,
    segmentable: true
  },
  {
    key: "chats",
    icon: "chat",
    labelKey: strings.DASH_CHATS,
    linkKey: strings.DASH_GO_TO_CHATS,
    route: Routes.CHATS,
    segmentable: false,
    noComparison: true
  },
  {
    key: "totalGardeners",
    icon: "users",
    labelKey: strings.DASH_TOTAL_GARDENERS,
    linkKey: strings.DASH_GO_TO_GARDENERS,
    route: Routes.GARDENER_REVIEW,
    segmentable: false
  }
];

function OperatorDashboard() {
  const dispatch = useDispatch();
  const history = useHistory();

  const statsByYear = useSelector((state) => state.dashboard.statsByYear || {});
  const availableYears = useSelector((state) => state.dashboard.availableYears || []);
  const fetchingYears = useSelector((state) => state.dashboard.fetchingYears || {});
  const unreadChats = useSelector((state) => state.chat.chats.filter(c => c.unread).length);

  const [selectedYear, setSelectedYear] = useState(YTD);

  useEffect(() => {
    dispatch(getDashboardStats(YTD, { showLoading: true }));
    const compYear = getComparisonYear(YTD);
    if (compYear) dispatch(getDashboardStats(compYear));
    dispatch(getChats());
  }, [dispatch]);

  const handleYearChange = useCallback((year) => {
    setSelectedYear(year);
    if (!statsByYear[year]) {
      dispatch(getDashboardStats(year));
    }
    const comparisonYear = getComparisonYear(year);
    if (comparisonYear && !statsByYear[comparisonYear]) {
      dispatch(getDashboardStats(comparisonYear));
    }
  }, [statsByYear, dispatch]);

  const yearOptions = buildYearOptions(availableYears);
  const comparisonYear = getComparisonYear(selectedYear);
  const selectedStats = statsByYear[selectedYear];
  const comparisonStats = statsByYear[comparisonYear];
  const isLoading = fetchingYears[selectedYear] && !selectedStats;

  const ytdStats = statsByYear[YTD];
  const trendData = (ytdStats?.monthlyTrend || []).map((m) => ({
    ...m,
    label: formatMonth(m.month)
  }));

  const vsLabel = comparisonYear
    ? `${i18nstring(strings.DASH_VS)} ${comparisonYear}`
    : null;

  return (
    <div className="operator-dashboard">
      <div className="dashboard-header">
        <div className="dashboard-header__subtitle">
          {i18nstring(strings.DASH_METRICS_SUBTITLE)}
        </div>
        <YearSelector
          years={yearOptions}
          selected={selectedYear}
          onChange={handleYearChange}
        />
      </div>

      <div className="stats-grid">
        {STAT_CARDS.map((card) => {
          let rawValue;
          if (card.key === "chats") {
            rawValue = unreadChats;
          } else if (card.segmentable) {
            rawValue = selectedStats?.[card.key];
          } else {
            rawValue = ytdStats?.[card.key];
          }
          const value = card.formatter
            ? card.formatter(rawValue)
            : (rawValue ?? "-");
          let delta = null;
          if (card.segmentable && !card.noComparison && vsLabel) {
            const d = computeDelta(selectedStats?.[card.key], comparisonStats?.[card.key]);
            if (d) {
              const magnitude = Math.abs(d.diff);
              const formattedMag = card.formatter ? card.formatter(magnitude) : String(magnitude);
              const text = d.direction === "down" ? formattedMag : `+${formattedMag}`;
              delta = { ...d, text, vsLabel };
            }
          }
          return (
            <StatCard
              key={card.key}
              icon={card.icon}
              value={value}
              loading={card.segmentable && isLoading}
              label={i18nstring(card.labelKey)}
              delta={delta}
              linkText={i18nstring(card.linkKey)}
              onLinkClick={() => history.push(card.route)}
            />
          );
        })}
      </div>

      {trendData.length > 0 && (
        <div className="trend-section">
          <h3>{i18nstring(strings.DASH_TREND_TITLE)}</h3>
          <div className="trend-chart">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <XAxis dataKey="label" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip />
                <Legend verticalAlign="top" align="right" iconType="line" wrapperStyle={{ paddingBottom: 12 }} />
                <Area
                  type="monotone"
                  dataKey="weight"
                  stroke="#5db99d"
                  fill="#5db99d"
                  fillOpacity={0.15}
                  strokeWidth={2}
                  name={i18nstring(strings.DASH_TREND_WEIGHT)}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#28a745"
                  fill="#28a745"
                  fillOpacity={0.15}
                  strokeWidth={2}
                  name={i18nstring(strings.DASH_TREND_DONATIONS)}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

export default OperatorDashboard;
